import { useMemo } from 'react';
import { useGetEventPhotoByEventHandleQuery } from './eventPhoto.graphql.generated';
import { extractHeaderLayoutPhotos } from './useEventPhoto';
import { isDefaultPlaceholderUrl } from './isDefaultPlaceholderUrl';
import { EventDesignPurpose } from '@graphql/generated-base';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { useEventMediaPhotos } from './useEventMediaPhotos';
import { isStudioAsset } from '@shared/utils/studioAsset';

type MainEventImage = Readonly<{ id: string; url: string }>;

type UseMainEventImagesResult = Readonly<{
  images: ReadonlyArray<MainEventImage>;
  loading: boolean;
}>;

/**
 * Picks up to `maxImages` images for the event, prioritising the user's
 * website header lineup, then filling the remaining slots from the photo album.
 * Album entries already present in the header (matched by id, then by url) are skipped.
 *
 * Requires event context (useEventInfo, useWebsitePublishCapabilityFeature) —
 * returns empty images and loading=false when no eventHandle is available.
 */
export const useMainEventImages = (maxImages: number = 3): UseMainEventImagesResult => {
  const { eventHandle } = useEventInfo();
  const { eventExposure } = useWebsitePublishCapabilityFeature();
  const shouldUseLivePurpose = eventExposure.value;

  const { data: eventData, loading: headerLoading } = useGetEventPhotoByEventHandleQuery({
    variables: {
      website: eventHandle ?? '',
      purpose: shouldUseLivePurpose ? EventDesignPurpose.live : EventDesignPurpose.draft,
    },
    skip: !eventHandle || eventExposure.isLoading,
    batchMode: 'fast',
  });

  const { sortedEventMediaPhotos, eventMediaLoading } = useEventMediaPhotos();

  const images = useMemo<ReadonlyArray<MainEventImage>>(() => {
    if (maxImages <= 0) return [];

    const headerPhotos = extractHeaderLayoutPhotos(eventData?.eventByName)
      .filter(p => !isDefaultPlaceholderUrl(p.url))
      .slice(0, maxImages);
    if (headerPhotos.length >= maxImages) {
      return headerPhotos.map(p => ({ id: p.id, url: p.url }));
    }

    const seenIds = new Set(headerPhotos.map(p => p.id));
    const seenUrls = new Set(headerPhotos.map(p => p.url));

    const combined: MainEventImage[] = headerPhotos.map(p => ({ id: p.id, url: p.url }));
    sortedEventMediaPhotos.forEach(album => {
      if (combined.length >= maxImages) return;
      const albumUrl = album.photo.url;
      if (seenIds.has(album.id) || seenUrls.has(albumUrl)) return;
      if (isStudioAsset(album.photo.assetId)) return;
      combined.push({ id: album.id, url: albumUrl });
      seenIds.add(album.id);
      seenUrls.add(albumUrl);
    });
    return combined;
  }, [eventData?.eventByName, sortedEventMediaPhotos, maxImages]);

  return { images, loading: headerLoading || eventMediaLoading || eventExposure.isLoading };
};
